// src/components/notifications/EnableNotificationsCard.jsx
// Guides users to turn on phone notifications.
//   • Android / desktop: one tap on "Enable Notifications".
//   • iPhone: web push only works from an INSTALLED PWA on iOS 16.4+, so when
//     the app isn't on the Home Screen yet we show the Add-to-Home-Screen steps
//     instead of a permission prompt (which silently no-ops in a Safari tab).
// Renders nothing once notifications are granted/dismissed, in local dev (FCM
// is disabled there), or where the browser has no Notification API.
import React, { useState } from "react";
import { Bell, Share, X } from "lucide-react";
import { toast } from "react-toastify";
import { getToken } from "firebase/messaging";
import { arrayUnion } from "firebase/firestore";
import { messaging } from "../../firebase/firebaseConfig";
import { updateItem } from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";

const VAPID_KEY =
  "BKNonFk-lAUXh-_LSJ6F1aoG1ZfVupq3qc3FsR2WgQw48IpnqLWRQ5MrYDqFzG4xNfAxovO6AB51e5lhp1JDG3Y";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent || "");
const isStandalone = () =>
  window.navigator?.standalone === true ||
  window.matchMedia?.("(display-mode: standalone)")?.matches === true;

export default function EnableNotificationsCard() {
  const [{ user }] = useStateValue();
  const supported = typeof window !== "undefined" && "Notification" in window;
  const [perm, setPerm] = useState(supported ? Notification.permission : "unsupported");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (process.env.NODE_ENV === "development") return null;
  if (!supported || dismissed || perm === "granted") return null;

  const ios = isIOS();
  const standalone = isStandalone();

  const enable = async () => {
    setBusy(true);
    try {
      const permission = await Notification.requestPermission();
      setPerm(permission);
      if (permission === "granted") {
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        const has = user?.NotificationToken?.find((t) => t == token);
        if (token && !has) {
          await updateItem("users", user?.id, {
            NotificationToken: arrayUnion(token),
          });
        }
        toast.success("Notifications On");
      }
    } catch (e) {
      console.error("Enable notifications failed:", e);
      toast.error("Couldn't enable notifications — try again");
    } finally {
      setBusy(false);
    }
  };

  const shell =
    "mx-3 mt-3 rounded-xl border border-indigo-200 bg-indigo-50/60 px-4 py-3 flex items-start gap-3";
  const iconWrap =
    "w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center flex-shrink-0";
  const dismissBtn =
    "ml-auto text-slate-400 hover:text-slate-600 flex-shrink-0 p-0.5";

  // iPhone, not yet installed — push isn't possible until it's on the Home Screen.
  if (ios && !standalone) {
    return (
      <div className={shell}>
        <div className={iconWrap}>
          <Bell size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            Get Notifications on Your iPhone
          </p>
          <p className="text-sm text-slate-600 mt-0.5">
            To get alerts on your phone, add this app to your Home Screen, then
            open it from there:
          </p>
          <ol className="text-sm text-slate-600 mt-1.5 space-y-0.5 list-decimal list-inside">
            <li className="inline-flex items-center gap-1">
              Tap the Share icon <Share size={14} className="inline" /> in
              Safari
            </li>
            <li>Choose "Add to Home Screen"</li>
            <li>Open Team Edelstein from the new icon, then allow notifications</li>
          </ol>
          <p className="text-xs text-slate-400 mt-1.5">
            Requires iPhone software (iOS) 16.4 or newer.
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className={dismissBtn}
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // Permission was blocked — can only be undone in the device settings.
  if (perm === "denied") {
    return (
      <div className={shell}>
        <div className={iconWrap}>
          <Bell size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">
            Notifications Are Turned Off
          </p>
          <p className="text-sm text-slate-600 mt-0.5">
            They're blocked for this app. Turn them back on in your device
            Settings → Notifications → Team Edelstein.
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className={dismissBtn}
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // Default — a deliberate one-tap enable (Android, desktop, installed iOS PWA).
  return (
    <div className={shell}>
      <div className={iconWrap}>
        <Bell size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">
          Turn On Notifications
        </p>
        <p className="text-sm text-slate-600 mt-0.5">
          Get alerted when a new lead comes in, a deal hits a milestone, or
          you're @mentioned.
        </p>
        <button
          onClick={enable}
          disabled={busy}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold bg-[#1E293B] text-white rounded-lg px-4 py-2 disabled:opacity-60"
        >
          {busy ? "Enabling…" : "Enable Notifications"}
        </button>
      </div>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className={dismissBtn}
      >
        <X size={16} />
      </button>
    </div>
  );
}
